import type { DecisionView } from "@mailhand/core/api";
import type { PolicyOutcome, Run } from "@mailhand/core/schemas";
import { RUN_STATE_LABEL } from "../../app/labels.js";
import { useTenant } from "../../app/tenant.js";
import { Badge, Card, CardHeader, type Tone } from "../../ui/index.js";
import { ConfidenceBar } from "./ConfidenceBar.js";

const OUTCOME: Record<PolicyOutcome, { label: string; tone: Tone }> = {
  run: { label: "Ran", tone: "green" },
  awaiting_approval: { label: "Needs approval", tone: "amber" },
  hold: { label: "Held", tone: "orange" },
  ignore: { label: "Ignored", tone: "grey" },
};

/** The latest run of the decided action shows where it is now; the policy outcome shows only where it started. */
function currentStatus(decision: DecisionView, runs: readonly Run[]): { label: string; tone: Tone } {
  const latestRun = runs.findLast((run) => run.actionId === decision.actionId);
  return latestRun === undefined ? OUTCOME[decision.policyOutcome] : RUN_STATE_LABEL[latestRun.state];
}

export function DecisionCard({ decision, runs }: { decision: DecisionView; runs: readonly Run[] }) {
  const { actions, view } = useTenant();
  const nameOf = (actionId: string): string => actions.find((action) => action.id === actionId)?.name ?? actionId;
  const outcome = currentStatus(decision, runs);
  return (
    <Card>
      <CardHeader eyebrow="Decision" title={decision.actionName} aside={<Badge tone={outcome.tone}>{outcome.label}</Badge>} />
      <ConfidenceBar value={decision.confidence} threshold={view.tenant.settings.threshold} />
      <p className="decision-reason">{decision.reason}</p>
      {decision.policyReason === undefined ? null : <p className="muted small">{decision.policyReason}</p>}
      {decision.suggestedActionId === undefined ? null : (
        <p className="muted small">Suggested: {nameOf(decision.suggestedActionId)}</p>
      )}
      {decision.alternatives === undefined || decision.alternatives.length === 0 ? null : (
        <div className="alternatives">
          <div className="eyebrow">Also considered</div>
          {decision.alternatives.map((alternative) => (
            <div key={alternative.actionId} className="alternative">
              <span>{nameOf(alternative.actionId)}</span>
              <span className="mono muted">{alternative.confidence.toFixed(2)}</span>
            </div>
          ))}
        </div>
      )}
      {Object.keys(decision.inputs).length === 0 ? null : (
        <dl className="kv kv-spaced">
          {Object.entries(decision.inputs).map(([name, value]) => (
            <div key={name} className="kv-row">
              <dt>{name}</dt>
              <dd>{String(value)}</dd>
            </div>
          ))}
        </dl>
      )}
    </Card>
  );
}
